import React from 'react';
import { cn } from '../../lib/utils';

/* StatusBadge — LED-toned status pill.
   Pattern: a glowing coloured dot followed by an uppercase mono label,
   wrapped in a hairline-bordered pill. Tones map to the field-system LEDs:

     ok    → green   (published, signal present)
     live  → orange  (active / now / primary action target)
     warn  → amber   (sync, attention)
     error → red     (failure, clip)
     off   → steel   (draft / inactive / muted) */

export type StatusBadgeTone = 'ok' | 'live' | 'warn' | 'error' | 'off';

interface StatusBadgeProps {
  tone?: StatusBadgeTone;
  children: React.ReactNode;
  className?: string;
}

type ToneStyle = { text: string; border: string; glow: string };

const toneStyles: Record<StatusBadgeTone, ToneStyle> = {
  ok: {
    text: 'var(--color-led-green)',
    border: 'rgba(58, 209, 122, 0.45)',
    glow: 'rgba(58, 209, 122, 0.55)',
  },
  live: {
    text: 'var(--color-led-orange)',
    border: 'rgba(255, 90, 31, 0.45)',
    glow: 'rgba(255, 90, 31, 0.55)',
  },
  warn: {
    text: 'var(--color-led-amber)',
    border: 'rgba(255, 174, 0, 0.45)',
    glow: 'rgba(255, 174, 0, 0.55)',
  },
  error: {
    text: 'var(--color-led-red)',
    border: 'rgba(255, 59, 48, 0.45)',
    glow: 'rgba(255, 59, 48, 0.55)',
  },
  off: {
    text: 'var(--color-grey-500)',
    border: 'var(--color-steel)',
    glow: 'transparent',
  },
};

export default function StatusBadge({ tone = 'off', children, className }: StatusBadgeProps) {
  const t = toneStyles[tone];
  const lit = tone !== 'off';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 px-2 py-0.5 rounded-sm border font-mono text-xs tracking-[0.18em] uppercase whitespace-nowrap leading-none',
        className
      )}
      style={{ color: t.text, borderColor: t.border }}
    >
      <span
        aria-hidden="true"
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{
          background: t.text,
          boxShadow: lit ? `0 0 6px ${t.glow}` : 'none',
        }}
      />
      {children}
    </span>
  );
}
